/**
 * Shared vocabulary for the RO status dashboard (WARP-943, WARP-957).
 *
 * Two sections, as Mark and Michelle set them on 2026-09-24, each adding up:
 *
 *   Top, summing to Received:
 *     Ready · Outstanding (pre-submission) · Closed · Closed and submitted · Internal
 *     (Outstanding expands by whose review it is: the admin's or the dealer's)
 *     (Internal expands into Internal itself and pre-submission write-offs)
 *
 *   Under Closed and submitted, summing to it:
 *     Pending OEM · Outstanding (returned) · Paid · Paid with adjustments · Written off
 *
 * A bucket is a set of WARP statuses plus whether the RO was ever submitted
 * (`previouslySubmitted`). That flag is what keeps an RO pulled back onto the
 * Outstanding List after submission in Closed and submitted, once — Mark: "Once there
 * was closed and submitted, it always counts as closed and submitted. One time." The API
 * uses the same definitions (RepairOrderViewSet.TOP_BUCKETS and friends).
 *
 * Keys are camelCase because every API response is camelCased on the way in, nested
 * keys included (`buckets.paid_adjusted` arrives as `buckets.paidAdjusted`).
 */

export const BUCKET = {
  RECEIVED: 'received',
  READY: 'ready',
  OUTSTANDING: 'outstanding',
  OUTSTANDING_ADMIN: 'outstandingAdmin',
  OUTSTANDING_DEALER: 'outstandingDealer',
  CLOSED: 'closed',
  SUBMITTED: 'submitted',
  INTERNAL: 'internal',
  INTERNAL_ONLY: 'internalOnly',
  WRITTEN_OFF_PRE: 'writtenOffPre',
  PENDING: 'pending',
  RETURNED: 'returned',
  PAID: 'paid',
  PAID_ADJUSTED: 'paidAdjusted',
  WRITTEN_OFF: 'writtenOff',
  // Everything on the Outstanding List, submitted before or not. Only for the flag on
  // ROs older than the prior year; not a row of its own.
  OUTSTANDING_ANY: 'outstandingAny',
}

/** The top section, in Mark's order. Received is their total, not one of them. */
export const TOP_BUCKETS = [
  BUCKET.READY,
  BUCKET.OUTSTANDING,
  BUCKET.CLOSED,
  BUCKET.SUBMITTED,
  BUCKET.INTERNAL,
]

/**
 * What the Outstanding row expands into: whose review an RO is waiting on. The admin side
 * is everything not in Dealer Review (WPC Review, and drafts not yet sent to the dealer),
 * so the two always add up to Outstanding. The API counts them the same way.
 */
export const OUTSTANDING_BUCKETS = [BUCKET.OUTSTANDING_ADMIN, BUCKET.OUTSTANDING_DEALER]

/** What the Internal row expands into. */
export const INTERNAL_BUCKETS = [BUCKET.INTERNAL_ONLY, BUCKET.WRITTEN_OFF_PRE]

/** Under Closed and submitted. Together these make it up. */
export const SUBMITTED_BUCKETS = [
  BUCKET.PENDING,
  BUCKET.RETURNED,
  BUCKET.PAID,
  BUCKET.PAID_ADJUSTED,
  BUCKET.WRITTEN_OFF,
]

const STATUS = {
  NOT_STARTED: 'Not Started',
  OUTSTANDING: 'On Outstanding List',
  CLOSED: 'Closed',
  SUBMITTED: 'Closed — Submitted',
  PAID: 'Paid',
  PAID_ADJUSTED: 'Paid with Adjustments',
  WRITTEN_OFF: 'Written Off',
  INTERNAL: 'Internal',
}

const DEALER_REVIEW = 'Dealer Review'

/**
 * The query behind each bucket, for the click-through list: WARP statuses (empty for
 * any), whether the RO was ever submitted (undefined for either), and Outstanding List
 * states to keep (`olStates`) or leave out (`excludeOlStates`).
 */
export const BUCKET_FILTERS = {
  [BUCKET.RECEIVED]: { statuses: [] },
  [BUCKET.READY]: { statuses: [STATUS.NOT_STARTED], previouslySubmitted: false },
  [BUCKET.OUTSTANDING]: { statuses: [STATUS.OUTSTANDING], previouslySubmitted: false },
  [BUCKET.OUTSTANDING_ADMIN]: {
    statuses: [STATUS.OUTSTANDING],
    previouslySubmitted: false,
    excludeOlStates: [DEALER_REVIEW],
  },
  [BUCKET.OUTSTANDING_DEALER]: {
    statuses: [STATUS.OUTSTANDING],
    previouslySubmitted: false,
    olStates: [DEALER_REVIEW],
  },
  [BUCKET.CLOSED]: { statuses: [STATUS.CLOSED], previouslySubmitted: false },
  [BUCKET.SUBMITTED]: { statuses: [], previouslySubmitted: true },
  [BUCKET.INTERNAL]: {
    statuses: [STATUS.INTERNAL, STATUS.WRITTEN_OFF],
    previouslySubmitted: false,
  },
  [BUCKET.INTERNAL_ONLY]: { statuses: [STATUS.INTERNAL], previouslySubmitted: false },
  [BUCKET.WRITTEN_OFF_PRE]: { statuses: [STATUS.WRITTEN_OFF], previouslySubmitted: false },
  [BUCKET.PENDING]: { statuses: [STATUS.SUBMITTED], previouslySubmitted: true },
  [BUCKET.RETURNED]: {
    statuses: [STATUS.NOT_STARTED, STATUS.OUTSTANDING, STATUS.CLOSED, STATUS.INTERNAL],
    previouslySubmitted: true,
  },
  [BUCKET.PAID]: { statuses: [STATUS.PAID], previouslySubmitted: true },
  [BUCKET.PAID_ADJUSTED]: { statuses: [STATUS.PAID_ADJUSTED], previouslySubmitted: true },
  [BUCKET.WRITTEN_OFF]: { statuses: [STATUS.WRITTEN_OFF], previouslySubmitted: true },
  [BUCKET.OUTSTANDING_ANY]: { statuses: [STATUS.OUTSTANDING] },
}

// The Outstanding List's review colors, as on its tabs and the bars on its rows: gold for
// WPC Review and blue for Dealer Review, for every user (tailwind gold-dark / gold-light /
// gold-900, blue-800 / blue-100 / blue-dark). Michelle: "gold's for us" (2026-10-01).
const REVIEW_TONES = {
  admin: { swatch: '#857347', fill: '#f5f0df', ink: '#62432c' },
  dealer: { swatch: '#045485', fill: '#e4effa', ink: '#1a4160' },
}

/**
 * Labels, one-line descriptions, and swatches (handoff spec §8). `description` takes the
 * OEM's name. `amount` names the dollars a cell shows, where WARP has them. The review
 * rows' text and colors depend on the dealer and the viewer: use `bucketMeta`.
 */
export const BUCKET_META = {
  [BUCKET.RECEIVED]: {
    label: 'Received',
    description: () => 'Total Logged to WPC',
    swatch: '#888780',
    fill: '#f1efe8',
    ink: '#2c2c2a',
  },
  [BUCKET.READY]: {
    label: 'Ready',
    description: () => 'Left to be worked',
    swatch: '#ef9f27',
    fill: '#faeeda',
    ink: '#633806',
  },
  [BUCKET.OUTSTANDING]: {
    label: 'Outstanding (pre-submission)',
    description: () => 'Needs dealer action',
    swatch: '#d85a30',
    fill: '#faece7',
    ink: '#712b13',
  },
  [BUCKET.OUTSTANDING_ADMIN]: {
    label: 'WPC review',
    description: () => 'Waiting on WPC',
    ...REVIEW_TONES.admin,
  },
  [BUCKET.OUTSTANDING_DEALER]: {
    label: 'Dealer review',
    description: () => 'Waiting on the dealer',
    ...REVIEW_TONES.dealer,
  },
  [BUCKET.CLOSED]: {
    label: 'Closed',
    description: () => 'Not yet submitted',
    swatch: '#1d9e75',
    fill: '#e1f5ee',
    ink: '#085041',
  },
  [BUCKET.SUBMITTED]: {
    label: 'Closed and submitted',
    description: (oem) => `Sent to ${oem}`,
    swatch: '#639922',
    fill: '#eaf3de',
    ink: '#27500a',
  },
  [BUCKET.INTERNAL]: {
    label: 'Internal',
    description: () => 'Not sent to the OEM',
    swatch: '#b4b2a9',
    fill: '#f1efe8',
    ink: '#444441',
  },
  [BUCKET.INTERNAL_ONLY]: {
    label: 'Internal',
    description: () => 'Handled inside the dealership',
    swatch: '#b4b2a9',
    fill: '#f1efe8',
    ink: '#444441',
  },
  [BUCKET.WRITTEN_OFF_PRE]: {
    label: 'Written off (pre-submission)',
    description: () => 'Closed, never submitted',
    swatch: '#e24b4a',
    fill: '#fcebeb',
    ink: '#791f1f',
    amount: 'lost',
    amountKey: 'writtenOffPre',
  },
  [BUCKET.PENDING]: {
    label: 'Pending OEM',
    description: (oem) => `Submitted, awaiting ${oem}`,
    swatch: '#85b7eb',
    fill: '#e6f1fb',
    ink: '#0c447c',
    amount: 'claimed',
    amountKey: 'pending',
  },
  [BUCKET.RETURNED]: {
    label: 'Outstanding (returned)',
    description: () => 'Back on the list after submission',
    swatch: '#d85a30',
    fill: '#faece7',
    ink: '#712b13',
  },
  [BUCKET.PAID]: {
    label: 'Paid',
    description: () => 'Paid in full',
    swatch: '#639922',
    fill: '#eaf3de',
    ink: '#27500a',
  },
  [BUCKET.PAID_ADJUSTED]: {
    label: 'Paid with adjustments',
    description: () => 'Paid less than claimed',
    swatch: '#ef9f27',
    fill: '#faeeda',
    ink: '#633806',
  },
  [BUCKET.OUTSTANDING_ANY]: {
    label: 'Outstanding',
    description: () => 'On the Outstanding List',
    swatch: '#d85a30',
    fill: '#faece7',
    ink: '#712b13',
  },
  [BUCKET.WRITTEN_OFF]: {
    label: 'Written off',
    description: () => 'Rejected after submission',
    swatch: '#e24b4a',
    fill: '#fcebeb',
    ink: '#791f1f',
    amount: 'lost',
    amountKey: 'writtenOff',
  },
}

/**
 * A bucket's meta for this dealer. Only the admin review row changes: on a self-managed
 * dealer (`Dealer.is_dealer_managed`) the review before the dealer's is the dealer's own
 * admin's, not WPC's, so it reads "Admin review" for everyone.
 */
export const bucketMeta = (bucket, { selfManaged = false } = {}) => {
  const meta = BUCKET_META[bucket]
  if (bucket === BUCKET.OUTSTANDING_ADMIN && selfManaged) {
    return { ...meta, label: 'Admin review', description: () => "Waiting on the dealer's admin" }
  }
  return meta
}

/**
 * The buckets one RO counts in, for the status pills in the RO list: where it sits in
 * the top section, and for anything ever submitted, where it sits beneath.
 */
export const bucketsForRo = (ro) => {
  const submitted = Boolean(ro.previouslySubmitted)
  switch (ro.status) {
    case STATUS.NOT_STARTED:
      return submitted ? [BUCKET.SUBMITTED, BUCKET.RETURNED] : [BUCKET.READY]
    case STATUS.OUTSTANDING:
      return submitted ? [BUCKET.SUBMITTED, BUCKET.RETURNED] : [BUCKET.OUTSTANDING]
    case STATUS.CLOSED:
      return submitted ? [BUCKET.SUBMITTED, BUCKET.RETURNED] : [BUCKET.CLOSED]
    case STATUS.SUBMITTED:
      return [BUCKET.SUBMITTED, BUCKET.PENDING]
    case STATUS.PAID:
      return [BUCKET.SUBMITTED, BUCKET.PAID]
    case STATUS.PAID_ADJUSTED:
      return [BUCKET.SUBMITTED, BUCKET.PAID_ADJUSTED]
    case STATUS.WRITTEN_OFF:
      return submitted ? [BUCKET.SUBMITTED, BUCKET.WRITTEN_OFF] : [BUCKET.WRITTEN_OFF_PRE]
    case STATUS.INTERNAL:
      return [BUCKET.INTERNAL_ONLY]
    default:
      return []
  }
}

export const SLA_LATE = { swatch: '#e24b4a', fill: '#fcebeb', ink: '#791f1f' }

/**
 * Buckets that are still open. Clicking one opens the aging strip; the others are
 * period totals or settled outcomes, so they do not.
 */
export const IN_PROGRESS_BUCKETS = [
  BUCKET.READY,
  BUCKET.OUTSTANDING,
  BUCKET.OUTSTANDING_ADMIN,
  BUCKET.OUTSTANDING_DEALER,
  BUCKET.CLOSED,
  BUCKET.PENDING,
  BUCKET.RETURNED,
]

/**
 * Not a column: everything received before the prior year, for the flag on Outstanding
 * List ROs older than the table reaches (2026-09-24). Its range comes from the summary.
 */
export const OLDER_PERIOD = { id: 'older', label: 'Before the prior year' }

export const PERIODS = [
  { id: 'today', label: 'Today', tint: '#ffffff' },
  { id: 'wtd', label: 'Week to date', tint: '#f7f8fb' },
  { id: 'mtd', label: 'Month to date', tint: '#faf8f2' },
  { id: 'ytd', label: 'Year to date', tint: '#f5f8f5' },
  { id: 'prior', label: 'Prior year', tint: '#f4f3ef', rule: true },
]

/** Aging bands, in days since received. Per-dealer configuration is a follow-up. */
export const AGING_BANDS = [
  { id: '0-30', label: '0–30 days', max: 30, tint: 'transparent' },
  { id: '31-90', label: '31–90 days', max: 90, tint: '#faf8f2' },
  { id: '91-365', label: '91–365 days', max: 365, tint: '#faeeda' },
  { id: '365+', label: 'Over 1 year', max: Infinity, tint: '#faece7' },
]
